import type { Note } from '../domain/note'
import { isGeneratedFallbackTitle } from '../domain/note'
import { hasSpecificLinkImage } from '../links/linkMetadata'
import { linkPreviewCategory } from '../links/linkPreviewCategory'
import {
  hideMarkdownPreviewArtifacts,
  removeAutoDerivedTitleLine,
  removeDuplicateLeadingHeading,
  removeUrlsForCardPreview,
} from '../utils/markdownText'
import type { NoteSearchResult } from './noteSearch'
import type { SearchLayout } from './layoutPreference'
import {
  truncateBrowseMarkdown,
  truncateBrowseText,
  type BrowseModePresentation,
} from './browsePresentation'

export type BrowseLinkTreatment = 'none' | 'ledger' | 'featured' | 'hybrid'

export interface BrowseCardModel {
  /** The note holds words of its own besides its links (not a shared page title). */
  hasAuthoredBody: boolean
  hasSingleLink: boolean
  isLinkOnlyPreview: boolean
  linkLimit: number
  linkTreatment: BrowseLinkTreatment
  previewMarkdown: string
  primaryLinkCategory: ReturnType<typeof linkPreviewCategory> | null
  shouldShowNoteTitle: boolean
  showImages: boolean
  showLinkPreviews: boolean
  title: string
  visibleTitle: string
}

function normalizedCardText(value: string): string {
  return value.toLocaleLowerCase().replace(/\s+/g, ' ').trim()
}

function differsFromCardTitle(value: string | null | undefined, title: string): value is string {
  return typeof value === 'string' && normalizedCardText(value) !== normalizedCardText(title)
}

const cardWords = (value: string) => value.toLocaleLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean)
const MARKDOWN_LINK_LINE = /^\[([^\]]*)\]\(https?:\/\/[^)\s]+(?:\s+"[^"]*")?\)$/
const BARE_URL_LINE = /^https?:\/\/\S+$/i

/**
 * A browser's share sheet sends the page title with the link, so a shared page is saved as
 * "<page title>" above "[label](url)" (or the bare address). That line is the page's words, not the owner's: a note
 * that is only it and its one link is still a link-only note and keeps the link's artwork.
 * The line must contain the link's name or closely match it (most of the shorter one's words
 * in common); a line the owner wrote about the link ("consider this chair…") never does.
 */
export function isSharedPageTitleBody(note: Pick<Note, 'body' | 'manualTitle' | 'annotation' | 'urls'>): boolean {
  if (note.manualTitle !== null || note.annotation.trim() || note.urls.length !== 1) return false
  const lines = note.body.trim().split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  if (lines.length !== 2 || /https?:\/\//i.test(lines[0]!)) return false
  const label = MARKDOWN_LINK_LINE.exec(lines[1]!)?.[1] ?? (BARE_URL_LINE.test(lines[1]!) ? '' : undefined)
  if (label === undefined) return false
  const line = cardWords(lines[0]!)
  return [label, note.urls[0]!.title ?? ''].some((name) => {
    const words = cardWords(name)
    if (!words.length || !line.length) return false
    const [shorter, longer] = words.length <= line.length ? [words, line] : [line, words]
    if (longer.join(' ').includes(shorter.join(' '))) return true
    const common = shorter.filter((word) => longer.includes(word)).length
    return shorter.length >= 2 && common / shorter.length >= 0.6
  })
}

function previewFallbackFromSearchSnippet(snippet: string, title: string): string {
  const trimmedSnippet = snippet.trim()
  if (!trimmedSnippet || trimmedSnippet === 'No text' || !differsFromCardTitle(trimmedSnippet, title)) return ''

  const trimmedTitle = title.trim()
  if (!trimmedSnippet.toLocaleLowerCase().startsWith(trimmedTitle.toLocaleLowerCase())) return trimmedSnippet

  return trimmedSnippet
    .slice(trimmedTitle.length)
    .replace(/^[\s:–—|\-]+/, '')
    .trim()
}

/**
 * Resolves the content recipe for one Browse card. Viewport geometry and phone
 * interaction policy remain CSS-owned; this model covers only note composition.
 */
export function buildBrowseCardModel({
  assetsCount,
  expectedAssetCount,
  layout,
  note,
  presentation,
  result,
}: {
  assetsCount: number
  expectedAssetCount: number
  layout: SearchLayout
  note: Note
  presentation: BrowseModePresentation
  result: NoteSearchResult
}): BrowseCardModel {
  const showImages = layout !== 'minimal'
  const showLinkPreviews = layout !== 'minimal' && presentation.maxLinkPreviews > 0
  const title = hideMarkdownPreviewArtifacts(note.manualTitle ?? note.derivedTitle).trim()
  const visibleTitle = truncateBrowseText(
    hideMarkdownPreviewArtifacts(result.title),
    presentation.maxTitleChars,
  )
  const hasSingleLink = note.urls.length === 1
  const authoredAnnotation = note.annotation.trim().length > 0 && differsFromCardTitle(note.annotation, title)
    ? note.annotation
    : undefined
  const matchingLinkDescription = note.urls[0]?.description
  const supportingText = authoredAnnotation ?? (
    differsFromCardTitle(matchingLinkDescription, title)
      && result.matchTerms.some((term) => matchingLinkDescription.toLowerCase().includes(term.toLowerCase()))
      ? matchingLinkDescription
      : undefined
  )
  const previewSource = supportingText ?? note.body
  const previewWithoutAutoTitle = !supportingText && note.manualTitle === null
    ? removeAutoDerivedTitleLine(previewSource, title)
    : previewSource
  const hasAuthoredBody = removeUrlsForCardPreview(note.body).trim().length > 0 && !isSharedPageTitleBody(note)
  const extractedPreviewMarkdown = removeUrlsForCardPreview(removeDuplicateLeadingHeading(
    previewWithoutAutoTitle,
    title,
  ))
  const previewMarkdown = extractedPreviewMarkdown || (
    hasAuthoredBody ? previewFallbackFromSearchSnippet(result.snippet, title) : ''
  )
  const limitedPreviewMarkdown = truncateBrowseMarkdown(previewMarkdown, presentation.maxTextChars)
  const hasVisibleTextPreview = limitedPreviewMarkdown.trim().length > 0
  const hasVisibleImagePreview = showImages && Math.max(expectedAssetCount, assetsCount) > 0
  const hasVisibleLinkPreview = showLinkPreviews && note.urls.length > 0
  const needsTitleFallback = !hasVisibleTextPreview
    && !hasVisibleImagePreview
    && !hasVisibleLinkPreview
  const shouldShowNoteTitle = visibleTitle.length > 0 && (
    layout === 'minimal'
    || (!isGeneratedFallbackTitle(note) && (!hasSingleLink || hasAuthoredBody))
    || needsTitleFallback
  )
  const isLinkOnlyPreview = showLinkPreviews
    && note.urls.length > 0
    && previewMarkdown.trim().length === 0
    && (hasSingleLink || note.manualTitle === null)
    && !hasAuthoredBody
    && assetsCount === 0
    && expectedAssetCount === 0
  const usesRichLinkTreatment = layout === 'detailed' || isLinkOnlyPreview
  const hasFeaturedLinkArtwork = note.urls.some((url) =>
    hasSpecificLinkImage(url, url.imageUrl),
  )
  const linkTreatment: BrowseLinkTreatment = !showLinkPreviews || note.urls.length === 0
    ? 'none'
    : !usesRichLinkTreatment
      ? 'ledger'
      : note.urls.length > 1
        ? hasFeaturedLinkArtwork
          ? 'hybrid'
          : 'ledger'
        : 'featured'

  return {
    hasAuthoredBody,
    hasSingleLink,
    isLinkOnlyPreview,
    linkLimit: Math.min(presentation.maxLinkPreviews, note.urls.length),
    linkTreatment,
    previewMarkdown: limitedPreviewMarkdown,
    primaryLinkCategory: showLinkPreviews && note.urls[0]
      ? linkPreviewCategory(note.urls[0], note.contentType)
      : null,
    shouldShowNoteTitle,
    showImages,
    showLinkPreviews,
    title,
    visibleTitle,
  }
}
